import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import { formatApiMessage } from '@/api/client';
import { type EnvelopeView, envelopesApi } from '@/api/envelopes.api';
import { foldersApi } from '@/api/folders.api';
import { type NumberingElement, type NumberingScheme, numberingSchemeApi } from '@/api/numbering-scheme.api';
import { numberingVocabApi, type VocabKind } from '@/api/numbering-vocab.api';
import { templatesApi } from '@/api/templates.api';
import { DocumentsStep } from '@/components/envelope/DocumentsStep';
import { FieldsStep, type FieldsStepHandle } from '@/components/envelope/FieldsStep';
import { RecipientsStep } from '@/components/envelope/RecipientsStep';
import { Stepper } from '@/components/envelope/Stepper';
import { AppShell } from '@/components/layout/AppShell';
import { useAuthStore } from '@/stores/auth.store';

const CODE_RE = /^[A-Za-z0-9]{0,12}$/;
const metaSchema = z.object({
  title: z.string().min(1, 'Required').max(300),
  message: z.string().max(2000).optional().or(z.literal('')),
  numberingType: z.string().regex(CODE_RE, 'Alphanumeric, max 12').optional().or(z.literal('')),
  numberingDept: z.string().regex(CODE_RE, 'Alphanumeric, max 12').optional().or(z.literal('')),
  numberingForm: z.string().regex(CODE_RE, 'Alphanumeric, max 12').optional().or(z.literal('')),
  numberingSchemeId: z.string().optional().or(z.literal('')),
  // Customer policy: document source, confidentiality and next-review date are
  // mandatory — a controlled document must never be filed without them.
  documentSource: z.enum(['Internal', 'External'], {
    errorMap: () => ({ message: 'Required' }),
  }),
  confidentiality: z.string().min(1, 'Required').max(40),
  nextReviewDate: z.string().min(1, 'Required'),
  folderId: z.string().optional().or(z.literal('')),
});
type MetaForm = z.infer<typeof metaSchema>;

export function EnvelopeNewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Support resuming an existing draft via `?continue={uuid}` — used by
  // newRevision() so R2 lands in the wizard at step 2 instead of stranding
  // the user on the detail page.
  const [searchParams] = useSearchParams();
  const continueId = searchParams.get('continue');
  const [envelopeId, setEnvelopeId] = useState<string | null>(continueId);
  const [stepIndex, setStepIndex] = useState(continueId ? 1 : 0);
  const fieldsRef = useRef<FieldsStepHandle | null>(null);
  const STEPS = [
    { key: 'meta',       label: t('wizard.step_meta') },
    { key: 'documents',  label: t('wizard.step_documents') },
    { key: 'recipients', label: t('wizard.step_recipients') },
    { key: 'fields',     label: t('wizard.step_fields') },
    { key: 'review',     label: t('wizard.step_review') },
  ];

  const { data: envelope } = useQuery({
    queryKey: ['envelopes', 'detail', envelopeId],
    queryFn: () => envelopesApi.get(envelopeId!),
    enabled: !!envelopeId,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<MetaForm>({ resolver: zodResolver(metaSchema) });
  const watchedType = watch('numberingType') ?? '';
  const watchedDept = watch('numberingDept') ?? '';
  const watchedForm = watch('numberingForm') ?? '';
  const watchedScheme = watch('numberingSchemeId') ?? '';

  // The org's numbering schemes (naming types). The wizard shows a dropdown
  // when more than one exists; the chosen scheme's format also decides which
  // of the source/dept/form codes are actually required.
  const { data: schemes = [] } = useQuery({
    queryKey: ['numbering-schemes'],
    queryFn: () => numberingSchemeApi.list(),
  });

  // Preselect the envelope's saved scheme, else the org default, once loaded.
  useEffect(() => {
    if (schemes.length === 0 || watchedScheme) return;
    const preferred =
      envelope?.numberingSchemeId ??
      schemes.find((s) => s.isDefault)?.id ??
      schemes[0]?.id;
    if (preferred) setValue('numberingSchemeId', preferred);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemes, envelope?.numberingSchemeId]);

  const activeFormat =
    schemes.find((s) => s.id === watchedScheme)?.format ??
    schemes.find((s) => s.isDefault)?.format ??
    schemes[0]?.format ??
    '';
  // When no scheme/format is known yet, fall back to requiring all three codes
  // (the legacy default format uses them). Otherwise require only the codes the
  // selected scheme's format references.
  const needs = {
    type: !activeFormat || /\{type\}/i.test(activeFormat),
    dept: !activeFormat || /\{dept\}/i.test(activeFormat),
    form: !activeFormat || /\{form\}/i.test(activeFormat),
  };

  // Slot elements of the selected scheme drive per-element fields in the wizard
  // (each maps onto {type}/{dept}/{form} by order): a 'list' shows a dropdown,
  // an 'input' shows a free-text box the user types into. When the scheme has
  // none we fall back to the vocabulary-based code pickers.
  const selectedScheme =
    schemes.find((s) => s.id === watchedScheme) ??
    schemes.find((s) => s.isDefault) ??
    schemes[0];
  const slotElements = ((selectedScheme?.elements ?? []) as NumberingElement[]).filter(
    (e): e is Extract<NumberingElement, { kind: 'list' | 'input' }> =>
      e.kind === 'list' || e.kind === 'input',
  );

  const createMutation = useMutation({
    mutationFn: (values: MetaForm) =>
      envelopesApi.create({
        title: values.title,
        message: values.message || undefined,
        workflowType: 'sequential',
        numberingSchemeId: values.numberingSchemeId || undefined,
      }).then(async (created) => {
        // Codes & folder go in a follow-up PATCH so envelopesApi.create's
        // signature stays minimal (title/message/workflow).
        const extras: Parameters<typeof envelopesApi.update>[1] = {};
        if (values.numberingType) extras.numberingType = values.numberingType;
        if (values.numberingDept) extras.numberingDept = values.numberingDept;
        if (values.numberingForm) extras.numberingForm = values.numberingForm;
        if (values.documentSource) extras.documentSource = values.documentSource;
        if (values.confidentiality) extras.confidentiality = values.confidentiality;
        if (values.folderId) extras.folderId = values.folderId;
        let env = created;
        if (Object.keys(extras).length > 0) {
          env = await envelopesApi.update(created.id, extras);
        }
        // Next-review date lives behind its own endpoint (tracks manual vs
        // Algebra source); set it after the draft exists.
        if (values.nextReviewDate) {
          env = await envelopesApi.setReviewDate(created.id, values.nextReviewDate);
        }
        return env;
      }),
    onSuccess: (env) => {
      setEnvelopeId(env.id);
      // Customer policy: cannot move to step 2 unless a document number has
      // been assigned. assign() is idempotent and runs in create() only when
      // the numbering codes resolve to a sequence — if they're missing we
      // hold the user on step 1.
      if (!env.documentNumber) {
        toast.error(t('wizard.numbering_required'));
        return;
      }
      setStepIndex(1);
      toast.success(t('wizard.draft_created'));
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const updateMetaMutation = useMutation({
    mutationFn: async (values: MetaForm) => {
      const env = await envelopesApi.update(envelopeId!, {
        title: values.title,
        message: values.message || undefined,
        // A revision keeps its predecessor's number — don't touch numbering.
        ...((envelope?.numberingRevision ?? 1) > 1
          ? {}
          : {
              numberingType: values.numberingType ?? null,
              numberingDept: values.numberingDept ?? null,
              numberingForm: values.numberingForm ?? null,
              numberingSchemeId: values.numberingSchemeId || null,
            }),
        documentSource: values.documentSource ?? null,
        confidentiality: values.confidentiality ?? null,
        folderId: values.folderId || null,
      });
      // Keep the mandatory next-review date in sync. Skip when the date is
      // Algebra-sourced (read-only mirror) and unchanged, so we don't flip it
      // to 'manual' behind Algebra's back.
      if (
        values.nextReviewDate &&
        !(envelope?.reviewDateSource === 'algebra' && envelope?.nextReviewDate?.slice(0, 10) === values.nextReviewDate)
      ) {
        return envelopesApi.setReviewDate(envelopeId!, values.nextReviewDate);
      }
      return env;
    },
    onSuccess: (env) => {
      qc.invalidateQueries({ queryKey: ['envelopes', 'detail', envelopeId] });
      if (!env.documentNumber) {
        toast.error(t('wizard.numbering_required'));
        return;
      }
      setStepIndex(1);
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  // "Update existing document" — pick a completed, numbered document from the
  // library and open a new revision of it (reuses the revision pipeline: the
  // number carries over, the old version auto-archives with a "NOT ACTUAL"
  // watermark on send). Only offered on a fresh new document.
  const [showUpdateExisting, setShowUpdateExisting] = useState(false);
  const newRevisionMutation = useMutation({
    mutationFn: (payload: { uuid: string; changeDescription: string }) =>
      envelopesApi.newRevision(payload.uuid, { changeDescription: payload.changeDescription }),
    onSuccess: (env) => {
      setShowUpdateExisting(false);
      qc.invalidateQueries({ queryKey: ['envelopes', 'list'] });
      // Land straight in the wizard on the new revision draft.
      navigate(`/envelopes/new?continue=${env.id}`);
      setEnvelopeId(env.id);
      setStepIndex(1);
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  // A revision inherits its predecessor's number — manual numbering is locked.
  const isRevision = (envelope?.numberingRevision ?? 1) > 1;

  const completedThrough = computeProgress(envelope);

  return (
    <AppShell>
      <button
        onClick={() => navigate('/envelopes')}
        className="text-sm text-muted hover:text-gray-900 mb-3"
      >
        {t('wizard.back_to_documents')}
      </button>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="page-title">{t('wizard.new_envelope_title')}</h1>
          <p className="page-subtitle">
            {envelope ? t('wizard.editing_draft', { title: envelope.title }) : t('wizard.step1_subtitle')}
          </p>
        </div>
        {envelope && (
          <button
            onClick={() => navigate(`/envelopes/${envelope.id}`)}
            className="btn btn-secondary"
          >
            {t('wizard.save_and_exit')}
          </button>
        )}
      </div>

      {envelopeId && envelope && envelope.numberingRevision > 1 && (
        <div className="card !p-3 mb-3 flex items-start gap-3 border-l-4 border-warning bg-warning-bg/40">
          <div className="text-xl">📝</div>
          <div className="flex-1 text-[13px] text-gray-900">
            <strong>{t('wizard.revision_banner_title', { revision: envelope.numberingRevision })}</strong>
            <div className="text-[12px] text-muted mt-0.5">
              {t('wizard.revision_banner_hint')}
            </div>
          </div>
        </div>
      )}

      {envelopeId && (
        <Stepper
          steps={STEPS}
          activeIndex={stepIndex}
          completedThrough={completedThrough}
          onJump={(i) => setStepIndex(i)}
        />
      )}

      <div className="card">
        {stepIndex === 0 && (
          <form
            onSubmit={handleSubmit((v) => {
              if (envelopeId) {
                updateMetaMutation.mutate(v);
              } else {
                createMutation.mutate(v);
              }
            })}
            className="space-y-4 max-w-2xl"
          >
            <h3 className="text-base font-semibold text-gray-900 mb-1">{t('wizard.envelope_details')}</h3>
            <p className="text-[13px] text-muted mb-4">{t('wizard.title_help')}</p>

            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <label className="label !mb-0">{t('wizard.title_label')}</label>
                {!envelopeId && (
                  <button
                    type="button"
                    onClick={() => setShowUpdateExisting(true)}
                    className="text-[12px] text-primary hover:underline font-medium"
                  >
                    🔄 {t('wizard.update_existing_btn')}
                  </button>
                )}
              </div>
              <input
                className="input"
                placeholder={t('wizard.title_placeholder')}
                defaultValue={envelope?.title}
                {...register('title')}
              />
              {errors.title && <p className="text-xs text-danger mt-1">{errors.title.message}</p>}
            </div>

            <div>
              <label className="label">
                {t('wizard.message_label')} <span className="text-muted font-normal">{t('wizard.message_help')}</span>
              </label>
              <textarea
                className="textarea min-h-[80px]"
                placeholder={t('wizard.message_placeholder')}
                defaultValue={envelope?.message ?? ''}
                {...register('message')}
              />
            </div>

            <FolderPicker
              register={register}
              setValue={setValue}
              defaultFolderId={envelope?.folder?.id ?? ''}
            />

            <DocumentSourcePicker
              register={register}
              defaultValue={envelope?.documentSource ?? ''}
              error={errors.documentSource?.message}
            />

            <ConfidentialityPicker
              register={register}
              defaultValue={envelope?.confidentiality ?? ''}
              error={errors.confidentiality?.message}
            />

            <div>
              <label className="label">
                {t('wizard.next_review_label', 'Next review date')}{' '}
                <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="input"
                defaultValue={envelope?.nextReviewDate ? envelope.nextReviewDate.slice(0, 10) : ''}
                {...register('nextReviewDate')}
              />
              {errors.nextReviewDate && (
                <p className="text-xs text-danger mt-1">{t('wizard.next_review_required', 'Next review date is required')}</p>
              )}
              <p className="help">{t('wizard.next_review_help', 'The date this document must be reviewed / re-validated.')}</p>
            </div>

            {isRevision ? (
              // Updating an existing document — the number carries over from
              // the previous version and manual numbering is locked.
              <div>
                <label className="label">{t('wizard.numbering_locked_label')}</label>
                <div className="input bg-gray-50 font-mono text-[13px] text-gray-900 flex items-center gap-2 cursor-not-allowed">
                  🔒 {envelope?.documentNumber ?? '—'}
                </div>
                <p className="help">{t('wizard.numbering_locked_hint')}</p>
              </div>
            ) : (
              <>
                {schemes.length > 0 && (
                  <SchemePicker
                    register={register}
                    schemes={schemes}
                    defaultValue={watchedScheme}
                  />
                )}

                {slotElements.length > 0 ? (
                  <SchemeListPicker
                    register={register}
                    slotElements={slotElements}
                    existingNumber={envelope?.documentNumber ?? null}
                    defaultValues={{
                      type: envelope?.numberingType ?? '',
                      dept: envelope?.numberingDept ?? '',
                      form: envelope?.numberingForm ?? '',
                    }}
                  />
                ) : (
                  <NumberingCodesBlock
                    register={register}
                    errors={errors}
                    defaultValues={{
                      type: envelope?.numberingType ?? '',
                      dept: envelope?.numberingDept ?? '',
                      form: envelope?.numberingForm ?? '',
                    }}
                    existingNumber={envelope?.documentNumber ?? null}
                    watchedType={watchedType}
                    watchedDept={watchedDept}
                    watchedForm={watchedForm}
                    needs={needs}
                  />
                )}

                <NumberPreview
                  format={activeFormat}
                  type={watchedType}
                  dept={watchedDept}
                  form={watchedForm}
                  seq={selectedScheme?.nextSeq ?? 1}
                  needs={needs}
                />
              </>
            )}

            <div className="flex justify-end pt-3 border-t border-border">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={createMutation.isPending || updateMetaMutation.isPending}
              >
                {createMutation.isPending || updateMetaMutation.isPending
                  ? t('common.saving')
                  : t('wizard.continue_to_documents')}
              </button>
            </div>
          </form>
        )}

        {stepIndex === 1 && envelope && (
          <>
            <DocumentsStep envelope={envelope} />
            <StepNav
              onBack={() => setStepIndex(0)}
              onNext={() => setStepIndex(2)}
              nextLabel={t('wizard.continue_to_recipients')}
              nextDisabled={envelope.documents.length === 0}
              nextDisabledHint={t('wizard.documents_required')}
            />
          </>
        )}

        {stepIndex === 2 && envelope && (
          <>
            <RecipientsStep envelope={envelope} />
            <StepNav
              onBack={() => setStepIndex(1)}
              onNext={() => setStepIndex(3)}
              nextLabel={t('wizard.continue_to_fields')}
              nextDisabled={envelope.recipients.length === 0}
              nextDisabledHint={t('wizard.recipients_required')}
            />
          </>
        )}

        {stepIndex === 3 && envelope && (
          <>
            <FieldsStep ref={fieldsRef} envelope={envelope} />
            <StepNav
              onBack={() => setStepIndex(2)}
              onNext={async () => {
                try {
                  const count = await fieldsRef.current?.saveIfDirty();
                  if (typeof count === 'number' && count === 0) {
                    const ok = confirm(t('wizard.no_fields_confirm'));
                    if (!ok) return;
                  }
                  setStepIndex(4);
                } catch (err) {
                  toast.error(formatApiMessage(err));
                }
              }}
              nextLabel={t('wizard.continue_to_review')}
            />
          </>
        )}

        {stepIndex === 4 && envelope && (
          <ReviewStep envelope={envelope} onBack={() => setStepIndex(3)} />
        )}
      </div>

      {showUpdateExisting && (
        <UpdateExistingModal
          onClose={() => setShowUpdateExisting(false)}
          onPick={(uuid, changeDescription) =>
            newRevisionMutation.mutate({ uuid, changeDescription })
          }
          pending={newRevisionMutation.isPending}
        />
      )}
    </AppShell>
  );
}

// Modal for "Update existing document": pick a completed, numbered document
// from the library, describe the change, and open a new revision of it.
function UpdateExistingModal({
  onClose,
  onPick,
  pending,
}: {
  onClose: () => void;
  onPick: (uuid: string, changeDescription: string) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<EnvelopeView | null>(null);
  const [changeDescription, setChangeDescription] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['envelopes', 'update-existing', search],
    queryFn: () =>
      envelopesApi.list({ status: 'completed', search: search || undefined, perPage: 50 }),
  });
  // Only numbered documents can be revised (newRevision requires a sequence).
  const items = (data?.items ?? []).filter((e) => !!e.documentNumber);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-base font-semibold text-gray-900">{t('wizard.update_existing_title')}</h3>
          <p className="text-[12px] text-muted mt-0.5">{t('wizard.update_existing_subtitle')}</p>
        </div>

        {!selected ? (
          <>
            <div className="px-5 py-3 border-b border-border">
              <input
                className="input"
                placeholder={t('wizard.update_existing_search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="text-center py-10 text-muted text-[13px]">{t('common.loading')}</div>
              ) : items.length === 0 ? (
                <div className="text-center py-10 text-muted text-[13px]">
                  {t('wizard.update_existing_empty')}
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {items.map((e) => (
                    <li key={e.id}>
                      <button
                        type="button"
                        onClick={() => setSelected(e)}
                        className="w-full text-left px-5 py-3 hover:bg-gray-50 flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="text-[13px] font-medium text-gray-900 truncate">{e.title}</div>
                          <code className="text-[11px] text-gray-600">{e.documentNumber}</code>
                        </div>
                        <span className="text-[11px] text-muted shrink-0">R{e.numberingRevision}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="px-5 py-3 border-t border-border flex justify-end">
              <button className="btn btn-secondary btn-sm" onClick={onClose}>
                {t('common.cancel')}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              <div className="bg-primary-50 border border-primary-100 rounded-lg p-3">
                <div className="text-[13px] font-medium text-gray-900">{selected.title}</div>
                <code className="text-[11px] text-gray-600">{selected.documentNumber}</code>
              </div>
              <div>
                <label className="label">{t('wizard.update_existing_change_label')}</label>
                <textarea
                  className="textarea min-h-[90px]"
                  placeholder={t('wizard.update_existing_change_ph')}
                  value={changeDescription}
                  onChange={(e) => setChangeDescription(e.target.value)}
                  autoFocus
                />
                <p className="help">{t('wizard.update_existing_change_hint')}</p>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-border flex justify-between">
              <button className="btn btn-secondary btn-sm" onClick={() => setSelected(null)}>
                {t('common.back')}
              </button>
              <button
                className="btn btn-primary btn-sm"
                disabled={pending || changeDescription.trim().length === 0}
                onClick={() => onPick(selected.id, changeDescription.trim())}
              >
                {pending ? t('common.saving') : t('wizard.update_existing_confirm')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FolderPicker({
  register,
  setValue,
  defaultFolderId,
}: {
  register: ReturnType<typeof useForm<MetaForm>>['register'];
  setValue: ReturnType<typeof useForm<MetaForm>>['setValue'];
  defaultFolderId: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  // Folder creation is admin-only; everyone may select an existing folder.
  const isAdmin = me?.role === 'owner' || me?.role === 'admin';
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const { data: folderData } = useQuery({
    queryKey: ['folders', 'list'],
    queryFn: () => foldersApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => foldersApi.create({ name }),
    onSuccess: (f) => {
      qc.invalidateQueries({ queryKey: ['folders', 'list'] });
      // Auto-select the freshly created folder in the form.
      setValue('folderId', f.id);
      setCreating(false);
      setNewName('');
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <div>
      <label className="label">{t('wizard.folder_label')}</label>
      <div className="flex items-center gap-2">
        <select
          className="input flex-1"
          defaultValue={defaultFolderId}
          {...register('folderId')}
        >
          <option value="">{t('wizard.folder_none')}</option>
          {folderData?.folders.map((f) => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
        </select>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setCreating((v) => !v)}
            className="btn btn-secondary btn-sm whitespace-nowrap"
          >
            + {t('wizard.new_folder')}
          </button>
        )}
      </div>
      {isAdmin && creating && (
        <div className="mt-2 flex items-center gap-2">
          <input
            autoFocus
            className="input"
            placeholder={t('wizard.folder_name_placeholder')}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (newName.trim()) createMutation.mutate(newName.trim());
              } else if (e.key === 'Escape') {
                setCreating(false);
                setNewName('');
              }
            }}
            maxLength={120}
          />
          <button
            type="button"
            onClick={() => newName.trim() && createMutation.mutate(newName.trim())}
            disabled={!newName.trim() || createMutation.isPending}
            className="btn btn-primary btn-sm"
          >
            {createMutation.isPending ? '…' : t('common.create')}
          </button>
        </div>
      )}
      <p className="help">{t('wizard.folder_help')}</p>
    </div>
  );
}

/**
 * Where the document comes from. Deliberately a closed two-value list (not the
 * admin-managed vocabulary that backs Confidentiality): "Internal vs External"
 * is a fixed property of a controlled document, not a per-company taxonomy.
 * Note this is NOT the numbering "source" code — that one feeds the document
 * number, this one only classifies the origin.
 */
function DocumentSourcePicker({
  register,
  defaultValue,
  error,
}: {
  register: ReturnType<typeof useForm<MetaForm>>['register'];
  defaultValue: string;
  error?: string;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <label className="label">
        {t('wizard.document_source_label', 'Document source')}{' '}
        <span className="text-danger">*</span>
      </label>
      <select className="input" defaultValue={defaultValue} {...register('documentSource')}>
        <option value="">{t('wizard.document_source_select', 'Select…')}</option>
        <option value="Internal">{t('wizard.document_source_internal', 'Internal')}</option>
        <option value="External">{t('wizard.document_source_external', 'External')}</option>
      </select>
      {error && (
        <p className="text-xs text-danger mt-1">
          {t('wizard.document_source_required', 'Document source is required')}
        </p>
      )}
      <p className="help">
        {t(
          'wizard.document_source_help',
          'Internal — produced by your company. External — received from a customer, supplier or regulator.',
        )}
      </p>
    </div>
  );
}

function ConfidentialityPicker({
  register,
  defaultValue,
  error,
}: {
  register: ReturnType<typeof useForm<MetaForm>>['register'];
  defaultValue: string;
  error?: string;
}) {
  const { t } = useTranslation();
  const { data: vocab } = useQuery({
    queryKey: ['numbering-vocab'],
    queryFn: () => numberingVocabApi.list(),
  });
  // Admin-managed confidentiality classifications take over when configured;
  // otherwise fall back to the built-in defaults so the field is never empty.
  const adminList = vocab?.confidentiality ?? [];
  const options =
    adminList.length > 0
      ? [
          { value: '', label: t('wizard.confidentiality_unset') },
          ...adminList.map((c) => {
            const val = (c.label || c.code).slice(0, 40);
            return { value: val, label: c.label ? `${c.label} (${c.code})` : c.code };
          }),
        ]
      : [
          { value: '', label: t('wizard.confidentiality_unset') },
          { value: 'Public', label: t('wizard.confidentiality_public') },
          { value: 'Internal', label: t('wizard.confidentiality_internal') },
          { value: 'Confidential', label: t('wizard.confidentiality_confidential') },
          { value: 'Strictly Confidential', label: t('wizard.confidentiality_strict') },
        ];
  return (
    <div>
      <label className="label">
        {t('wizard.confidentiality_label')} <span className="text-danger">*</span>
      </label>
      <select
        className="input"
        defaultValue={defaultValue}
        {...register('confidentiality')}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.value === '' ? t('wizard.confidentiality_select', 'Select…') : o.label}
          </option>
        ))}
      </select>
      {error && <p className="text-xs text-danger mt-1">{t('wizard.confidentiality_required', 'Confidentiality is required')}</p>}
      <p className="help">{t('wizard.confidentiality_help')}</p>
    </div>
  );
}

/**
 * Dropdown to pick which numbering scheme (naming type) the document is
 * numbered under. Only rendered when the org has at least one scheme.
 */
function SchemePicker({
  register,
  schemes,
  defaultValue,
}: {
  register: ReturnType<typeof useForm<MetaForm>>['register'];
  schemes: NumberingScheme[];
  defaultValue: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="border-t border-border pt-4">
      <label className="label">
        {t('wizard.numbering_scheme_label')} <span className="text-danger">*</span>
      </label>
      <select className="input" value={defaultValue} {...register('numberingSchemeId')}>
        {schemes.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
            {s.isDefault ? ` — ${t('wizard.numbering_scheme_default_hint')}` : ''}
          </option>
        ))}
      </select>
      <p className="help">{t('wizard.numbering_scheme_help')}</p>
    </div>
  );
}

/**
 * Big, friendly live preview of the document number the current selections will
 * produce. Replaces the need for users to read the raw {placeholder} format —
 * they just see the number take shape as they fill the dropdowns.
 */
function NumberPreview({
  format,
  type,
  dept,
  form,
  seq,
  needs,
}: {
  format: string;
  type: string;
  dept: string;
  form: string;
  seq: number;
  needs: { type: boolean; dept: boolean; form: boolean };
}) {
  const { t } = useTranslation();
  const missing =
    (needs.type && !type) || (needs.dept && !dept) || (needs.form && !form);
  // An empty format means "no scheme yet" — let the backend fall back to the
  // org's default format rather than showing a stuck placeholder.
  const fmt = format || undefined;
  const { data } = useQuery({
    queryKey: ['numbering-preview', format, type, dept, form, seq],
    queryFn: () => numberingSchemeApi.preview({ format: fmt, type, dept, form, seq }),
    enabled: !missing,
    staleTime: 30_000,
  });
  return (
    <div className="border-t border-border pt-4">
      <div className="rounded-lg border border-primary/30 bg-primary-50/40 p-3">
        <div className="text-[11px] uppercase font-semibold text-primary/80 tracking-wider mb-1">
          {t('wizard.numbering_live_label')}
        </div>
        {missing ? (
          <div className="text-[13px] text-muted">{t('wizard.numbering_live_pending')}</div>
        ) : (
          <>
            <code className="text-[18px] font-semibold text-gray-900 font-mono break-all">
              {data?.number ?? '…'}
            </code>
            <p className="text-[11px] text-muted mt-1">{t('wizard.numbering_live_hint')}</p>
          </>
        )}
      </div>
    </div>
  );
}

function NumberingCodesBlock({
  register,
  errors,
  defaultValues,
  existingNumber,
  watchedType,
  watchedDept,
  watchedForm,
  needs,
}: {
  register: ReturnType<typeof useForm<MetaForm>>['register'];
  errors: ReturnType<typeof useForm<MetaForm>>['formState']['errors'];
  defaultValues: { type: string; dept: string; form: string };
  existingNumber: string | null;
  watchedType: string;
  watchedDept: string;
  watchedForm: string;
  needs: { type: boolean; dept: boolean; form: boolean };
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Customer policy: numbering is MANDATORY. No toggle, no collapse —
  // wizard step 2 is gated on documentNumber being assigned and the
  // backend rejects assignment when source/dept/form are blank.

  // When codes are filled, look up matching templates and offer to use one —
  // this is the customer's "per-document-type approval chain" workflow.
  const { data: matches = [] } = useQuery({
    queryKey: ['templates', 'match', watchedType, watchedDept, watchedForm],
    queryFn: () =>
      templatesApi.match({
        type: watchedType || undefined,
        dept: watchedDept || undefined,
        form: watchedForm || undefined,
      }),
    enabled: !!(watchedType || watchedDept || watchedForm),
    staleTime: 30_000,
  });

  return (
    <div className="border-t border-border pt-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[13px] font-semibold text-gray-900 m-0">
          {t('wizard.numbering_section')} <span className="text-danger">*</span>
        </h4>
        {existingNumber ? (
          <code className="text-[11px] bg-success-bg text-success border border-success/30 rounded px-2 py-0.5">
            ✓ {existingNumber}
          </code>
        ) : (
          <span className="text-[11px] text-warning-fg">
            {t('wizard.numbering_required_inline')}
          </span>
        )}
      </div>

      {matches.length > 0 && (
        <div className="mt-2 p-3 bg-info-bg border border-info rounded-md text-[12px] text-info-fg">
          <div className="font-medium mb-1">📋 {t('wizard.template_suggestion')}</div>
          <div className="space-y-1">
            {matches.slice(0, 3).map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-2">
                <span className="truncate">
                  <strong>{m.title}</strong>
                  <span className="text-muted"> · {m.recipients.length} {t('dashboard.roles_short')}</span>
                </span>
                <button
                  type="button"
                  onClick={() => navigate('/templates', { state: { autoInstantiate: m.id } })}
                  className="text-primary hover:underline whitespace-nowrap"
                >
                  {t('wizard.use_template')} →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        {needs.type && (
          <VocabPicker
            kind="type"
            label={t('wizard.numbering_type')}
            placeholder="INT"
            hint={t('wizard.numbering_type_hint')}
            defaultValue={defaultValues.type}
            register={register}
            field="numberingType"
            error={errors.numberingType?.message}
            required
          />
        )}
        {needs.dept && (
          <VocabPicker
            kind="dept"
            label={t('wizard.numbering_dept')}
            placeholder="GRC"
            hint={t('wizard.numbering_dept_hint')}
            defaultValue={defaultValues.dept}
            register={register}
            field="numberingDept"
            error={errors.numberingDept?.message}
            required
          />
        )}
        {needs.form && (
          <VocabPicker
            kind="form"
            label={t('wizard.numbering_form')}
            placeholder="FRM"
            hint={t('wizard.numbering_form_hint')}
            defaultValue={defaultValues.form}
            register={register}
            field="numberingForm"
            error={errors.numberingForm?.message}
            required
          />
        )}
        {!needs.type && !needs.dept && !needs.form && (
          <p className="col-span-3 text-[12px] text-muted">{t('wizard.numbering_no_codes')}</p>
        )}
        <p className="col-span-3 text-[11px] text-muted">{t('wizard.numbering_help')}</p>
      </div>
    </div>
  );
}

// Field order the slot elements map onto (mirrors the format's
// {type}/{dept}/{form} slots on the backend).
const LIST_FIELDS = ['numberingType', 'numberingDept', 'numberingForm'] as const;

// Keep free-text slot values in the same shape the number expects: uppercase,
// alphanumeric, short (matches the metaSchema CODE_RE on submit).
const sanitizeSlot = (v: string) => v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);

/**
 * Renders one field per slot element of the chosen scheme, mapped in order onto
 * numberingType/Dept/Form. A 'list' element is a dropdown (e.g. Company →
 * İsoSofts/Spektr Group); an 'input' element is a free-text box the user types
 * into (e.g. another company's code "FHN"), not restricted to a fixed list.
 */
function SchemeListPicker({
  register,
  slotElements,
  defaultValues,
  existingNumber,
}: {
  register: ReturnType<typeof useForm<MetaForm>>['register'];
  slotElements: Array<Extract<NumberingElement, { kind: 'list' | 'input' }>>;
  defaultValues: { type: string; dept: string; form: string };
  existingNumber: string | null;
}) {
  const { t } = useTranslation();
  const defaults = [defaultValues.type, defaultValues.dept, defaultValues.form];
  return (
    <div className="border-t border-border pt-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[13px] font-semibold text-gray-900 m-0">
          {t('wizard.numbering_section')} <span className="text-danger">*</span>
        </h4>
        {existingNumber ? (
          <code className="text-[11px] bg-success-bg text-success border border-success/30 rounded px-2 py-0.5">
            ✓ {existingNumber}
          </code>
        ) : (
          <span className="text-[11px] text-warning-fg">{t('wizard.numbering_required_inline')}</span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {slotElements.slice(0, 3).map((el, i) => {
          const reg = register(LIST_FIELDS[i]);
          return (
            <div key={i}>
              <label className="label">
                {el.name || `Element ${i + 1}`}
                <span className="text-danger ml-0.5">*</span>
              </label>
              {el.kind === 'input' ? (
                <input
                  className="input font-mono uppercase"
                  defaultValue={defaults[i] ?? ''}
                  placeholder={el.placeholder || ''}
                  maxLength={12}
                  {...reg}
                  onChange={(e) => {
                    e.target.value = sanitizeSlot(e.target.value);
                    void reg.onChange(e);
                  }}
                />
              ) : (
                <select className="input" defaultValue={defaults[i] ?? ''} {...reg}>
                  <option value="">{t('wizard.numbering_select_placeholder')}</option>
                  {el.options.map((o) => (
                    <option key={o.code} value={o.code}>
                      {o.label} ({o.code})
                    </option>
                  ))}
                </select>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-muted mt-2">{t('wizard.numbering_help')}</p>
    </div>
  );
}

function StepNav({
  onBack,
  onNext,
  nextLabel,
  nextDisabled,
  nextDisabledHint,
}: {
  onBack: () => void;
  onNext: () => void;
  nextLabel: string;
  nextDisabled?: boolean;
  nextDisabledHint?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-4 mt-6 pt-5 border-t border-border">
      <button onClick={onBack} className="btn btn-secondary">{t('common.back')}</button>
      <div className="flex items-center gap-3">
        {nextDisabled && nextDisabledHint && (
          <span className="text-[12px] text-muted">{nextDisabledHint}</span>
        )}
        <button onClick={onNext} className="btn btn-primary" disabled={nextDisabled}>
          {nextLabel}
        </button>
      </div>
    </div>
  );
}

function ReviewStep({ envelope, onBack }: { envelope: EnvelopeView; onBack: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sendMutation = useMutation({
    mutationFn: () => envelopesApi.send(envelope.id),
    onSuccess: () => {
      toast.success(t('wizard.envelope_sent'));
      navigate(`/envelopes/${envelope.id}`);
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const canSend = envelope.documents.length > 0 && envelope.recipients.length > 0;

  return (
    <div>
      <h3 className="text-base font-semibold text-gray-900 mb-1">{t('wizard.review_title')}</h3>
      <p className="text-[13px] text-muted mb-4">{t('wizard.review_subtitle')}</p>

      <div className="space-y-4">
        <div className="card !p-4">
          <div className="text-[11px] uppercase font-semibold text-muted mb-1">{t('wizard.review_doc_name')}</div>
          <div className="font-medium text-gray-900">{envelope.title}</div>
          {envelope.documentNumber && (
            <code className="text-[12px] font-mono text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded mt-1 inline-block">
              {envelope.documentNumber}
            </code>
          )}
          {envelope.message && (
            <p className="text-[13px] text-muted mt-2">{envelope.message}</p>
          )}
        </div>

        <div className="card !p-4">
          <div className="text-[11px] uppercase font-semibold text-muted mb-2">
            {t('wizard.review_documents', { count: envelope.documents.length })}
          </div>
          {envelope.documents.map((d) => (
            <div key={d.id} className="text-[13px] text-gray-900">
              {d.originalName} <span className="text-muted">· {d.pagesCount} {t('wizard.pages')}</span>
            </div>
          ))}
        </div>

        <div className="card !p-4">
          <div className="text-[11px] uppercase font-semibold text-muted mb-2">
            {t('wizard.review_recipients', {
              count: envelope.recipients.length,
              workflow: t(`envelope.workflow_${envelope.workflowType}`),
            })}
          </div>
          {envelope.recipients
            .slice()
            .sort((a, b) => a.signingOrder - b.signingOrder)
            .map((r) => (
              <div key={r.id} className="flex items-center gap-2 text-[13px] text-gray-900 py-0.5">
                <span className="text-muted w-6">#{r.signingOrder}</span>
                <span className="font-medium">{r.name}</span>
                <span className="text-muted">{r.email}</span>
                <span className="badge badge-muted">{t(`envelope.role_${r.role}`)}</span>
              </div>
            ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 mt-6 pt-5 border-t border-border">
        <button onClick={onBack} className="btn btn-secondary">{t('common.back')}</button>
        <button
          onClick={() => {
            if (confirm(t('wizard.send_confirm', { title: envelope.title, count: envelope.recipients.length }))) {
              sendMutation.mutate();
            }
          }}
          className="btn btn-primary"
          disabled={!canSend || sendMutation.isPending}
        >
          {sendMutation.isPending ? t('common.sending') : t('wizard.send_envelope')}
        </button>
      </div>
    </div>
  );
}

function computeProgress(envelope: EnvelopeView | undefined): number {
  if (!envelope) return 0;
  let progress = 1; // meta complete
  if (envelope.documents.length > 0) progress = 2;
  if (envelope.recipients.length > 0) progress = 3;
  // Fields can stay empty until just before sending — Step 3 is unlocked
  // as soon as recipients exist, and Step 4 (review) is reached when the
  // user advances. We don't gate progress on field count.
  return progress;
}

/**
 * Dropdown picker backed by the admin-managed numbering vocabulary.
 * Regular users pick from the existing codes (no free-text entry).
 * Owners and admins also see a "+ Yeni" link that opens an inline form
 * so they can extend the dictionary on the spot. Customer requirement.
 *
 * Falls back to a plain text input when the vocabulary endpoint fails
 * or hasn't been seeded yet — keeps the wizard usable on day-zero.
 */
function VocabPicker({
  kind,
  label,
  placeholder,
  hint,
  defaultValue,
  register,
  field,
  error,
  required,
}: {
  kind: VocabKind;
  label: string;
  placeholder: string;
  hint: string;
  defaultValue: string;
  register: ReturnType<typeof useForm<MetaForm>>['register'];
  field: 'numberingType' | 'numberingDept' | 'numberingForm';
  error?: string;
  required?: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const isAdmin = me?.role === 'owner' || me?.role === 'admin';

  const { data: vocab } = useQuery({
    queryKey: ['numbering-vocab'],
    queryFn: () => numberingVocabApi.list(),
  });
  const items = vocab?.[kind] ?? [];

  const [adding, setAdding] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const createMutation = useMutation({
    mutationFn: () =>
      numberingVocabApi.create({
        kind,
        code: newCode.trim().toUpperCase(),
        label: newLabel.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['numbering-vocab'] });
      setAdding(false);
      setNewCode('');
      setNewLabel('');
      toast.success(t('vocab.added'));
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const isEmpty = items.length === 0;

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1">
        <label className="label !mb-0">
          {label}
          {required && <span className="text-danger ml-0.5">*</span>}
        </label>
        {isAdmin && !adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="text-[11px] font-medium text-primary bg-primary-50 hover:bg-primary-100 px-2 py-0.5 rounded border border-primary/20"
          >
            + {t(`vocab.add_${kind}`)}
          </button>
        )}
      </div>

      {adding ? (
        <div className="border border-primary rounded-md p-2 space-y-1 bg-primary-50/50">
          <input
            value={newCode}
            onChange={(e) => setNewCode(e.target.value.toUpperCase().slice(0, 20))}
            placeholder={placeholder}
            className="input text-[12px]"
            maxLength={20}
            autoFocus
          />
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder={t('vocab.label_placeholder') as string}
            className="input text-[12px]"
            maxLength={200}
          />
          <div className="flex gap-1 justify-end">
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setNewCode('');
                setNewLabel('');
              }}
              className="text-[11px] text-muted hover:text-gray-900 px-2 py-1"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => createMutation.mutate()}
              disabled={!newCode || createMutation.isPending}
              className="btn btn-primary btn-sm"
            >
              {createMutation.isPending ? '…' : t('common.save')}
            </button>
          </div>
        </div>
      ) : isEmpty ? (
        // Empty-state CTA: replace the dropdown with a clear call to action
        // so members see what they need to do, and admins can act immediately.
        <div className="border-2 border-dashed border-warning rounded-md p-2.5 text-center bg-warning-bg/30">
          <div className="text-[11px] text-warning-fg font-medium">
            {t('vocab.empty_state')}
          </div>
          {isAdmin ? (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="btn btn-primary btn-sm mt-1.5"
            >
              + {t(`vocab.add_${kind}`)}
            </button>
          ) : (
            <div className="text-[10px] text-muted mt-1">
              {t('vocab.empty_state_member')}
            </div>
          )}
        </div>
      ) : (
        <select
          className={`input ${required && !defaultValue ? 'border-warning' : ''}`}
          defaultValue={defaultValue}
          {...register(field)}
        >
          <option value="">— {t('vocab.pick_one')} —</option>
          {items.map((v) => (
            <option key={v.id} value={v.code}>
              {v.code}
              {v.label ? ` — ${v.label}` : ''}
            </option>
          ))}
        </select>
      )}
      {error && <p className="text-xs text-danger mt-1">{error}</p>}
      <p className="help">{hint}</p>
    </div>
  );
}
